import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  Building,
  Mail,
  Phone,
  Globe,
  MapPin,
  TrendingUp,
  TrendingDown,
  Activity,
  Star,
  Eye,
  Edit,
  Trash2,
  MessageSquare,
  Rocket,
  ChevronDown,
  ExternalLink,
  BarChart3,
  DollarSign,
  Calendar,
  Zap,
  Target,
  Sparkles,
  Award,
  Clock
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Customer interface - define it here since the import path may not exist
interface Customer {
  id: string;
  company: {
    name: string;
    industry: string;
    size: string;
    location: string;
    website?: string;
  };
  primaryContact: {
    name: string;
    email: string;
    title: string;
  };
  status: string;
  totalCampaigns?: number;
  totalRevenue?: number;
  lastActivity?: string;
  createdAt: string;
  updatedAt: string;
}

interface CustomerStats {
  totalCustomers: number;
  activeCustomers: number;
  prospectCustomers: number;
  totalRevenue: number;
  avgCampaignsPerCustomer: number;
  avgLifetimeValue: number;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = "" 
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
}> = ({ title, value, change, icon: Icon, gradient, description }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Customer Card Component
const CustomerCard: React.FC<{
  customer: Customer;
  onViewDetails: (customer: Customer) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onNewCampaign: (customer: Customer) => void;
}> = ({ customer, onViewDetails, onEdit, onDelete, onNewCampaign }) => {
  const [showActions, setShowActions] = useState(false);

  const getCompanyInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'prospect':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'inactive':
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
      case 'churned':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      default:
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      {/* Customer Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-semibold">
              {getCompanyInitials(customer.company.name)}
            </span>
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white mb-1 truncate">
              {customer.company.name}
            </h3>
            <p className="text-neutral-400 text-sm mb-2">
              {customer.company.industry} • {customer.company.size}
            </p>
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              getStatusColor(customer.status)
            )}>
              <div className="w-2 h-2 rounded-full bg-current mr-2" />
              {customer.status}
            </div>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/95 backdrop-blur-xl border border-neutral-700 rounded-xl shadow-2xl z-10"
              >
                <div className="p-2">
                  <button
                    onClick={() => onViewDetails(customer)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">View Details</span>
                  </button>
                  <button
                    onClick={() => onEdit(customer)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm">Edit</span>
                  </button>
                  <button
                    onClick={() => onNewCampaign(customer)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors text-left"
                  >
                    <Rocket className="w-4 h-4" />
                    <span className="text-sm">New Campaign</span>
                  </button>
                  <div className="border-t border-neutral-700 my-1" />
                  <button
                    onClick={() => onDelete(customer)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Delete</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Customer Details */}
      <div className="space-y-3">
        <div className="flex items-center text-neutral-400 text-sm">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="truncate">{customer.company.location}</span>
        </div>
        
        {customer.primaryContact.email && (
          <div className="flex items-center text-neutral-400 text-sm">
            <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{customer.primaryContact.email}</span>
          </div>
        )}

        {customer.company.website && (
          <div className="flex items-center text-neutral-400 text-sm">
            <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{customer.company.website}</span>
          </div>
        )}
      </div>

      {/* Customer Metrics */}
      <div className="mt-4 pt-4 border-t border-neutral-700/50">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-lg font-semibold text-white">
              {customer.totalCampaigns || 0}
            </p>
            <p className="text-xs text-neutral-400">Campaigns</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-green-400">
              ${customer.totalRevenue?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-neutral-400">Revenue</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-blue-400">
              {customer.lastActivity ? new Date(customer.lastActivity).toLocaleDateString() : 'Never'}
            </p>
            <p className="text-xs text-neutral-400">Last Activity</p>
          </div>
        </div>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

export const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [stats, setStats] = useState<CustomerStats>({
    totalCustomers: 0,
    activeCustomers: 0,
    prospectCustomers: 0,
    totalRevenue: 0,
    avgCampaignsPerCustomer: 0,
    avgLifetimeValue: 0
  });

  const loadCustomers = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockCustomers: Customer[] = [
        {
          id: '1',
          company: {
            name: 'CSF RACE',
            industry: 'Automotive/Manufacturing',
            size: 'Medium Business (51-200 employees)',
            location: 'Detroit, MI',
            website: 'https://csfrace.com/',
          },
          primaryContact: {
            name: 'John Smith',
            email: 'john@csfrace.com',
            title: 'CEO'
          },
          status: 'prospect',
          totalCampaigns: 0,
          totalRevenue: 0,
          lastActivity: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '2',
          company: {
            name: 'Rippling',
            industry: 'Technology',
            size: 'Enterprise (1000+ employees)',
            location: 'San Francisco, CA',
            website: 'https://rippling.com/',
          },
          primaryContact: {
            name: 'Sarah Johnson',
            email: 'sarah@rippling.com',
            title: 'VP of Sales'
          },
          status: 'prospect',
          totalCampaigns: 0,
          totalRevenue: 0,
          lastActivity: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '3',
          company: {
            name: 'Test3',
            industry: 'Technology',
            size: 'Startup (1-10 employees)',
            location: 'Austin, TX',
            website: 'https://website.com/test3',
          },
          primaryContact: {
            name: 'Mike Chen',
            email: 'mike@test3.com',
            title: 'Founder'
          },
          status: 'prospect',
          totalCampaigns: 0,
          totalRevenue: 0,
          lastActivity: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];
      
      setCustomers(mockCustomers);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Simulate API call
      const mockStats: CustomerStats = {
        totalCustomers: 7,
        activeCustomers: 0,
        prospectCustomers: 7,
        totalRevenue: 0,
        avgCampaignsPerCustomer: 0,
        avgLifetimeValue: 0
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  const handleViewDetails = (customer: Customer) => {
    console.log('View details for:', customer.company.name);
  };

  const handleEdit = (customer: Customer) => {
    console.log('Edit customer:', customer.company.name);
  };

  const handleDelete = (customer: Customer) => {
    console.log('Delete customer:', customer.company.name);
  };

  const handleNewCampaign = (customer: Customer) => {
    console.log('New campaign for:', customer.company.name);
  };

  const handleCustomerUpdated = () => {
    loadCustomers();
    loadStats();
  };

  const handleCampaignCreated = () => {
    loadCustomers();
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         customer.company.industry.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || customer.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    All: customers.length,
    Active: customers.filter(c => c.status === 'active').length,
    Prospect: customers.filter(c => c.status === 'prospect').length,
    Inactive: customers.filter(c => c.status === 'inactive').length,
    Churned: customers.filter(c => c.status === 'churned').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Customer Lists
            </h1>
            <p className="text-xl text-neutral-400">
              Manage your customer relationships and track engagement
            </p>
          </div>
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Customer
          </Button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Customers"
            value={stats.totalCustomers}
            change="+23% this month"
            icon={Users}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All customer accounts"
          />
          <MetricCard
            title="Active Customers"
            value={stats.activeCustomers}
            change="+5.2% vs last month"
            icon={Activity}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Actively engaged"
          />
          <MetricCard
            title="Total Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            change="+28% this quarter"
            icon={DollarSign}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="From all customers"
          />
          <MetricCard
            title="Avg Deal Size"
            value={`$${stats.avgLifetimeValue.toLocaleString()}`}
            change="+12% improvement"
            icon={Target}
            gradient="bg-gradient-to-r from-orange-500 to-red-500"
            description="Average customer value"
          />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search customers..."
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  />
              </div>
              
            {/* Status Filter Tabs */}
            <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
              {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                    className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    statusFilter === status
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                  )}
                >
                  {status}
                  {count > 0 && (
                    <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                      {count}
                    </span>
                  )}
                  </button>
                ))}
              </div>
            </div>
        </motion.div>

        {/* Customer Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-400">Loading customers...</p>
            </div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-2">No customers found</h3>
            <p className="text-neutral-400 mb-6">
              {searchQuery ? 'Try adjusting your search criteria' : 'Start by adding your first customer'}
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Customer
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <CustomerCard
                  customer={customer}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onNewCampaign={handleNewCampaign}
        />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}; 